import * as logger from 'universal-logger';
import { defineMessages } from 'react-intl';
import { initialize, SubmissionError } from 'redux-form';
import { createFormAction } from 'redux-form-saga';
import { all, call, put, select, spawn, takeEvery } from 'redux-saga/effects';
import { getUserLocation, gtmSafeRedirect } from 'redux/saga/helpers';
import { getSelectorHtml } from 'helpers/getSelectorHtml';
import { friendbuyCode as getFriendbuyCode } from 'redux/saga/selectors/friendbuy';
import { deepAssign, deepExtract } from 'helpers/deep';
import { ga } from 'helpers/multitrack';
import { segment } from 'helpers/segment';
import { getForApi as getUtmValues } from 'helpers/utmValues';
import { loadAccountCreationConsent, transformConsentForApi } from 'components/Consent/Consent.saga';
import {
  authorizeFacebook,
  createUserAndLogIn,
  doLoginWithFacebook,
  getFacebookProfile,
} from 'features/Login/Authentication.saga';
import { postSignupTracking } from 'features/Signup/Signup.saga';
import { ERROR_SIGNUP_FB_EXISTING_ACCOUNT, ERROR_SIGNUP_FB_MISSING_EMAIL } from 'constants/api-error-codes';

const messages = defineMessages({
  contactSupport: {
    id: 'facebook-signup.contact-support',
    defaultMessage:
      'Sorry, we’re unable to create your account at this time. Please, <a target="_blank" rel="noopener noreferrer" href="/contact">reach out to us</a> for account help.',
    hasHtml: true,
  },
  missingEmail: {
    id: 'facebook-signup.missing-email',
    defaultMessage: 'Your Facebook account must have an email to sign up. Please add one or sign up via email.',
  },
});

export const FACEBOOK_FORM = 'signup-card-facebook';
export const facebookFormAction = createFormAction(FACEBOOK_FORM);

export const initializeFacebookForm = hiddenFields => ({
  type: `INITIALIZE_${FACEBOOK_FORM}`,
  hiddenFields,
});

export function* buildAccountDetails(client, form) {
  const renderedTerms = yield call(getSelectorHtml, '.SignupCard__terms__text > span');
  const [activeLocation, consents, conversion, friendbuyCode] = yield all([
    call(getUserLocation, client),
    call(transformConsentForApi, { ...form, terms: renderedTerms }),
    call(getUtmValues),
    select(getFriendbuyCode),
  ]);

  // Ensure RAF leads are correctly tracked
  if (friendbuyCode) {
    deepAssign('utm.utm_term', conversion, friendbuyCode);
  }

  return {
    consents,
    conversion,
    newsletter_subscribed: true,
    gls_location_id: deepExtract('location.id', activeLocation, null),
  };
}

export function buildProduct(form) {
  const productKey = {
    offer: 'msa_offer_id',
    plan: 'plan_id',
  }[form.productType];

  return productKey ? { [productKey]: form.productId } : {};
}

export function* createAccount(client, facebookToken, accountDetails, product) {
  try {
    return yield call(createUserAndLogIn, client, facebookFormAction, {
      ...accountDetails,
      ...product,
      facebook: { token: facebookToken },
    });
  } catch (error) {
    const errorCode = deepExtract('code', error);
    let message;

    if (errorCode === ERROR_SIGNUP_FB_EXISTING_ACCOUNT) {
      yield call(doLoginWithFacebook, client, null, facebookToken);
      yield call(gtmSafeRedirect, '/');
      return;
    }
    if (errorCode === ERROR_SIGNUP_FB_MISSING_EMAIL) {
      message = messages.missingEmail;
    } else {
      yield spawn(logger.error, 'Failed to create account with facebook token', error);
      message = messages.contactSupport;
    }

    return Promise.reject(message);
  }
}

export function* doInitializeFacebookForm(client, { hiddenFields }) {
  const consent = yield call(loadAccountCreationConsent, client);
  yield put(
    initialize(FACEBOOK_FORM, {
      ...hiddenFields,
      marketing: true,
      marketingDisclaimer: null,
      searchable: true,
      searchableDisclaimer: null,
      terms: consent.facebookDisclaimer,
    })
  );
}

export function* doSubmitFacebookForm(client, { payload: form }) {
  try {
    const FB = (client && client.FB) || window.FB;
    const facebookToken = yield call(authorizeFacebook, client);
    const accountDetails = yield call(buildAccountDetails, client, form);
    const product = yield call(buildProduct, form);
    const userId = yield call(createAccount, client, facebookToken, accountDetails, product);
    const profile = yield call(getFacebookProfile, FB);
    yield call(postSignupTracking, 'facebook', profile, userId);
    yield call(segment.track, 'Membership Purchase', {
      action: 'Membership Purchase Account Creation',
    });
    yield call(ga, 'Membership Purchase', 'Membership Purchase Account Creation');
    yield put(facebookFormAction.success());
  } catch (error) {
    const message = deepExtract('message', error);
    yield call(segment.track, 'Membership Purchase', {
      action: 'Membership Purchase Account Creation Error',
      label: message || 'Unknown Error',
    });
    yield call(ga, 'Membership Purchase', 'Membership Purchase Account Creation Error', message || 'Unknown Error');
    yield put(
      facebookFormAction.failure(
        new SubmissionError({
          _error: message || messages.contactSupport,
        })
      )
    );
  }
}

export function* watchFacebookFormSagas(client) {
  yield takeEvery(initializeFacebookForm().type, doInitializeFacebookForm, client);
  yield takeEvery(facebookFormAction.REQUEST, doSubmitFacebookForm, client);
}
